import {
  useEffect,
  useLayoutEffect,
  useRef,
  type RefObject,
} from "react";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

function captureActiveFocus(fallback: HTMLElement): HTMLElement {
  return document.activeElement instanceof HTMLElement
    ? document.activeElement
    : fallback;
}

function isDisplayed(el: HTMLElement): boolean {
  return el.offsetWidth > 0 || el.offsetHeight > 0 || el.getClientRects().length > 0;
}

/** Visible, enabled tab targets inside a modal panel — queried after commit, not from render-time refs. */
function collectPanelFocusables(panel: HTMLElement): HTMLElement[] {
  const nodes = panel.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
  const focusables: HTMLElement[] = [];
  for (const el of nodes) {
    if (el.closest("[hidden]") || !isDisplayed(el)) {
      continue;
    }
    focusables.push(el);
  }
  return focusables;
}

function useTabTrap(
  panelRef: RefObject<HTMLElement | null>,
  isOpen: boolean,
): void {
  useEffect(() => {
    const panel = panelRef.current;
    if (!panel) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (!isOpen || event.key !== "Tab") {
        return;
      }

      const focusables = collectPanelFocusables(panel);
      if (focusables.length === 0) {
        return;
      }

      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
        return;
      }

      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    panel.addEventListener("keydown", handleKeyDown);
    return () => {
      panel.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, panelRef]);
}

export function useLandingModalState(
  isOpen: boolean,
  returnFocusRef: RefObject<HTMLElement | null>,
  restoreFocus = true,
) {
  const panelRef = useRef<HTMLDivElement>(null);
  const focusBeforeOpenRef = useRef<HTMLElement | null>(null);
  const wasOpenRef = useRef(false);

  useLayoutEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        focusBeforeOpenRef.current = captureActiveFocus(
          returnFocusRef.current ?? document.body,
        );
      }
      wasOpenRef.current = true;
      return;
    }

    if (wasOpenRef.current) {
      if (restoreFocus) {
        focusBeforeOpenRef.current?.focus();
      }
      focusBeforeOpenRef.current = null;
    }
    wasOpenRef.current = false;
  }, [isOpen, restoreFocus, returnFocusRef]);

  return { panelRef };
}

export function useLandingModalFocusTrap(
  panelRef: RefObject<HTMLDivElement | null>,
  isOpen: boolean,
): void {
  useTabTrap(panelRef, isOpen);
}

export function useFocusOnOpen(
  isOpen: boolean,
  focusRef: RefObject<HTMLElement | null>,
  extraDeps: readonly unknown[] = [],
): void {
  useLayoutEffect(() => {
    if (!isOpen) {
      return;
    }

    focusRef.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- extraDeps drive sub-view focus changes
  }, [focusRef, isOpen, ...extraDeps]);
}

export function useEscapeToDismiss(isOpen: boolean, onDismiss: () => void): void {
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") {
        return;
      }

      event.preventDefault();
      onDismiss();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onDismiss]);
}
